import { CSSProperties, FC, ReactNode, useMemo } from 'react';
import { Portal } from '@mui/material';

import { ForceMobileProvider } from '@mopla/utils';

import { IMplPageHeaderProps, MplPageHeader } from '../MplPageHeader';

import { IsInBottomSliderContext } from './context';
import { ChildrenWrapper, ContentWrapper, Root } from './homeBanner.styles';

interface BottomSliderProps {
	open: boolean;
	children: ReactNode;
	title?: string;
	onBack?: () => void;
	onClose?: () => void;
	withHeader?: boolean;
	headerVariant?: IMplPageHeaderProps['variant'];
	headerSize?: IMplPageHeaderProps['size'];
	testId?: string;
	backgroundColor?: CSSProperties['backgroundColor'];
	disablePortal?: boolean;
	zIndex?: number;
}

export const BottomSlider: FC<BottomSliderProps> = ({
	children,
	open,
	onBack,
	onClose,
	title,
	withHeader = true,
	testId = 'bottomSlider-root',
	backgroundColor,
	disablePortal,
	zIndex,
	headerVariant,
	headerSize = 'small',
}) => {
	const ownerState = useMemo(
		() => ({
			open,
			withHeader,
			backgroundColor,
			zIndex,
		}),
		[open, withHeader, backgroundColor, zIndex]
	);

	return (
		<Portal disablePortal={disablePortal}>
			<IsInBottomSliderContext.Provider value={true}>
				<Root
					data-testid={testId}
					ownerState={ownerState}
					className="force-mobile"
				>
					<ContentWrapper
						ownerState={ownerState}
						elevation={0}
						square
						className="withSafeAreaTop"
					>
						{withHeader && title && (
							<MplPageHeader
								showBack={!!onBack}
								showClose={!!onClose}
								onClose={onClose}
								onBack={onBack}
								title={title}
								open={open}
								variant={headerVariant}
								size={headerSize}
							/>
						)}
						{/*TODO unmounting the content on "open=false" prevents the close animation to work properly*/}
						{open && (
							<ForceMobileProvider value={true}>
								<ChildrenWrapper ownerState={ownerState}>
									{children}
								</ChildrenWrapper>
							</ForceMobileProvider>
						)}
					</ContentWrapper>
				</Root>
			</IsInBottomSliderContext.Provider>
		</Portal>
	);
};
